import { redirect } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';

import { refreshServerFn } from './auth/refresh';

import type { AppFeatures } from '@/context/FeatureContext';
import { serverEnv } from '@/env';
import { sessionMiddleware } from '@/middleware/session';
import { userInfoMiddleware } from '@/middleware/user-info';
import { userHasRole, userHasRoles } from '@/utils/roles';
import { updateSessionDataHeaders } from '@/utils/session';

export const getUserInfo = createServerFn()
  .middleware([userInfoMiddleware])
  .handler(async ({ data, context: { session, user } }) => {
    if (!session.authentication) {
      throw redirect({
        to: '/login',
        search: { redirectTo: data?.redirectTo },
        headers: updateSessionDataHeaders(new Headers(), { ...session, authentication: undefined }),
      });
    }

    if (session.authentication.expiresAt <= Date.now()) {
      await refreshServerFn({ data: { redirectTo: data?.redirectTo } });
    }

    return {
      user,
      tenantId: session.tenantId,
    };
  });

export const getUserSession = createServerFn()
  .middleware([sessionMiddleware])
  .handler(async ({ context }) => {
    const session = context.session;
    return session;
  });

export const getUserAppFeatures = createServerFn()
  .middleware([sessionMiddleware, userInfoMiddleware])
  .handler(async ({ context: { user } }): Promise<AppFeatures> => {
    return {
      route_access: {
        '/conversation': serverEnv.FEATURE_CONVERSATION && userHasRole(user, 'chat-access'),
        // Skills Hub is a chat-domain surface; keep it gated exactly like chat so moving it
        // off /conversation to its own /skills route didn't widen access. Without this entry
        // `isRouteEnabled('/skills')` and `AppAccess` both default open to every tenant.
        '/skills': serverEnv.FEATURE_CONVERSATION && userHasRole(user, 'chat-access'),
        '/knowledge-base':
          serverEnv.FEATURE_KNOWLEDGE_BASE && userHasRoles(user, ['kb-editor', 'kb-reviewer', 'kb-admin']),
        '/loom': userHasRoles(user, [
          'workflow-view',
          'workflow-create',
          'workflow-execute',
          'workflow-manage',
          'workflow-delete',
          'workflow-admin',
        ]),
        '/code-modernization':
          serverEnv.FEATURE_CODE_REMEDY && userHasRoles(user, ['code-remediation-access', 'cr-lead-access']),
        '/workbench': serverEnv.FEATURE_WORKBENCH && userHasRole(user, 'editor-access'),
        '/view-remediation':
          serverEnv.FEATURE_VIEW_REMEDIATION &&
          userHasRoles(user, [
            'view-remediation-access',
            'view-remediation-viewer-access',
            'view-remediation-lead-access',
            'view-remediation-reviewer',
            'view-remediation-admin',
          ]),
        '/nexus': userHasRoles(user, [
          'nexus-view',
          'nexus-create',
          'nexus-edit',
          'nexus-delete',
          'nexus-admin',
          'nexus-approve',
        ]),
        // Every authenticated user may see Systems; admin-only actions gate in-page
        // and the SHOW_SYSTEMS feature flag hides the section entirely.
        '/systems': true,
        '/data-foundry': serverEnv.FEATURE_DATA_FOUNDRY && userHasRole(user, 'foundry-view'),
        '/editor': serverEnv.FEATURE_EDITOR && userHasRole(user, 'editor-access'),
        '/data-harmonization':
          serverEnv.FEATURE_DATA_HARMONIZATION &&
          userHasRoles(user, [
            'data-harmonization-view',
            'data-harmonization-create',
            'data-harmonization-edit',
            'data-harmonization-delete',
            'data-harmonization-super-admin',
          ]),
        '/migration-assessment': userHasRoles(user, [
          'integration-migration-view',
          'integration-migration-create',
          'integration-migration-deploy',
          'integration-migration-developer',
        ]),
        '/admin': serverEnv.FEATURE_ADMIN && userHasRole(user, 'admin'),
        '/platform': serverEnv.FEATURE_RELEASES && userHasRole(user, 'platform-admin'),
      },
      allow_role_override: {
        // Allow DH to be overriden
        '/data-harmonization': serverEnv.FEATURE_DATA_HARMONIZATION,
      },
    };
  });
